import React from 'react';
import { X } from 'lucide-react';
import { useCompareTray } from '../../context/CompareTrayContext';
import { COMPARE_FIELDS, KIND_LABEL } from '../../data/researchCatalog';

/**
 * CompareModal - renders the compare tray side-by-side as a field table.
 * Field rows come from COMPARE_FIELDS[kind]. Fully generic across kinds.
 */
const CompareModal = () => {
  const { items, kind, setOpen, remove } = useCompareTray();
  const fields = COMPARE_FIELDS[kind] || [];
  const kindLabel = KIND_LABEL[kind] || 'items';

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="compare-modal"
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-white w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[88vh] rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8e8ed] flex-shrink-0">
          <h2 className="text-[17px] font-semibold text-[#1d1d1f]">
            Comparing {items.length} {kindLabel}
          </h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close compare"
            data-testid="compare-modal-close"
            className="w-9 h-9 rounded-full hover:bg-[#f5f5f7] flex items-center justify-center text-[#86868b]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full border-collapse min-w-[520px]">
            <thead>
              <tr className="sticky top-0 bg-white z-10">
                <th className="text-left text-[11px] uppercase tracking-wide text-[#86868b] font-semibold p-3 w-36 border-b border-[#e8e8ed]" />
                {items.map((it) => (
                  <th key={it.__id} className="p-3 align-top border-b border-[#e8e8ed] border-l">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-left">
                        {it.__logo && (
                          <img src={it.__logo} alt="" className="h-7 max-w-[90px] object-contain mb-1.5" />
                        )}
                        <div className="text-[13.5px] font-semibold text-[#1d1d1f] leading-tight">{it.__label}</div>
                        {it.__sub && <div className="text-[11.5px] text-[#86868b] mt-0.5">{it.__sub}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(it.__id)}
                        aria-label="Remove from compare"
                        className="text-[#c0c0c5] hover:text-[#1d1d1f] flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {it.__href && (
                      <a
                        href={it.__href}
                        className="inline-block mt-2 text-[12px] font-semibold text-[#1d8efe] hover:underline"
                      >
                        View details →
                      </a>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map((f, idx) => (
                <tr key={f.label} className={idx % 2 ? 'bg-[#fafafa]' : ''}>
                  <td className="p-3 text-[12px] font-semibold text-[#52525b] align-top">{f.label}</td>
                  {items.map((it) => (
                    <td key={it.__id} className="p-3 text-[13px] text-[#1d1d1f] align-top border-l border-[#f0f0f2]">
                      {f.get(it) || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompareModal;
